import { FeatureCollection, LineString } from "geojson";
import { Layer, Source } from "react-map-gl";
import { makeTaskRenderer } from "../TaskLayers";

import { TASK_BASE_PROPS, TaskFeatureProperties } from "common/utils/rtcJobs";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import React from "react";

// This is a fallback for when there is truly no width data supplied to this component.
// It helps when troubleshooting because a lack of rig or job config data will
// result in very obviously weird looking tasks.
export const DEFAULT_ROW_WIDTH = 0.5;

// Default width for Transport jobs. We don't have a rig for these, but we do want to
// give them a reasonable width so they're visible on the map.
export const DEFAULT_TRANSPORT_WIDTH = 6;

enum LayerIds {
  ARROWS = "fft-arrows",
  CENTERLINE = "fft-centerline",
  PROGRESS_ARROWS = "fft-progress-arrows",
  PROGRESS_LINE = "fft-progress-line",
}

export const renderFollowFurrowTask = makeTaskRenderer({
  detailsField: "followFurrow",
  Render: React.memo(({ tasks, extraLayerProps, beforeId }) => {
    const centerlineData: FeatureCollection<LineString, TaskFeatureProperties> =
      {
        type: "FeatureCollection",
        features: tasks.flatMap((t) => {
          return {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates:
                t.followFurrow.path?.points.map((pt) => [pt.lng, pt.lat]) ?? [],
            },
            properties: {
              ...TASK_BASE_PROPS,
              task: t,
            },
          };
        }),
      };

    const progressData: FeatureCollection<LineString, TaskFeatureProperties> = {
      type: "FeatureCollection",
      features: tasks.flatMap((t) => {
        return {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates:
              t.progressPath?.points.map((pt) => [pt.lng, pt.lat]) ?? [],
          },
          properties: {
            ...TASK_BASE_PROPS,
            task: t,
          },
        };
      }),
    };

    return (
      <>
        <Source data={centerlineData} type="geojson">
          <Layer
            id={LayerIds.ARROWS}
            beforeId={beforeId}
            type="symbol"
            layout={{
              "symbol-placement": "line",
              "symbol-spacing": 50,
              "text-field": "▶",
              "text-size": 40,
              "text-keep-upright": false,
              "text-rotation-alignment": "map",
            }}
            paint={{
              "text-color": theme.colors.black,
            }}
            {...extraLayerProps}
          />
          <Layer
            id={LayerIds.CENTERLINE}
            beforeId={LayerIds.ARROWS}
            type="line"
            paint={{
              "line-color": theme.colors.black,
            }}
            {...extraLayerProps}
          />
        </Source>
        <Source data={progressData} type="geojson">
          <Layer
            id={LayerIds.PROGRESS_ARROWS}
            beforeId={beforeId}
            type="symbol"
            layout={{
              "symbol-placement": "line",
              "symbol-spacing": 50,
              "text-field": "▶",
              "text-size": 40,
              "text-keep-upright": false,
              "text-rotation-alignment": "map",
            }}
            paint={{
              "text-color": theme.colors.green[500],
            }}
            {...extraLayerProps}
          />
          <Layer
            id={LayerIds.PROGRESS_LINE}
            beforeId={LayerIds.PROGRESS_ARROWS}
            type="line"
            paint={{
              "line-color": theme.colors.green[500],
            }}
            {...extraLayerProps}
          />
        </Source>
      </>
    );
  }),
  interactiveLayerIds: [
    LayerIds.ARROWS,
    LayerIds.PROGRESS_ARROWS,
    LayerIds.CENTERLINE,
    LayerIds.PROGRESS_LINE,
  ],
  imagesToLoad: [],
});
